/**
 * Recently Viewed + Wishlist Stores.
 *
 * Pro Agent (subdomain) im localStorage. Wishlist + Recently Viewed sind
 * komplett clientseitig — kein Backend-Call, kein Login noetig.
 */
import { useEffect, useState } from "react";

export type RecentItem = {
  wc_product_id: number;
  name: string;
  image_url: string | null;
  price: string | null;
  viewed_at: number;  // unix-ms
};

export type WishlistItem = {
  wc_product_id: number;
  name: string;
  image_url: string | null;
  price: string | null;
  permalink: string;
  added_at: number;
};

const RV_PREFIX = "dp-rv-v1::";
const WL_PREFIX = "dp-wl-v1::";
const MAX_RECENT = 12;

function rvKey(agent: string) {
  return `${RV_PREFIX}${agent.toLowerCase()}`;
}
function wlKey(agent: string) {
  return `${WL_PREFIX}${agent.toLowerCase()}`;
}

function loadList<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function saveList<T>(key: string, items: T[]) {
  try {
    localStorage.setItem(key, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent("dp-discovery-changed", { detail: { key } }));
  } catch {
    /* quota */
  }
}

// ---- Recently Viewed ----

export function pushRecent(agentCode: string, item: Omit<RecentItem, "viewed_at">): void {
  const key = rvKey(agentCode);
  const list = loadList<RecentItem>(key);
  const filtered = list.filter((it) => it.wc_product_id !== item.wc_product_id);
  filtered.unshift({ ...item, viewed_at: Date.now() });
  saveList(key, filtered.slice(0, MAX_RECENT));
}

export function getRecent(agentCode: string, exclude: number | null = null): RecentItem[] {
  const list = loadList<RecentItem>(rvKey(agentCode));
  return exclude != null ? list.filter((it) => it.wc_product_id !== exclude) : list;
}

export function useRecentViewed(agentCode: string, excludeId: number | null = null) {
  const [items, setItems] = useState<RecentItem[]>(() => getRecent(agentCode, excludeId));
  useEffect(() => {
    setItems(getRecent(agentCode, excludeId));
    const onChange = (e: Event) => {
      const ev = e as CustomEvent<{ key: string }>;
      if (ev.detail?.key === rvKey(agentCode)) {
        setItems(getRecent(agentCode, excludeId));
      }
    };
    window.addEventListener("dp-discovery-changed", onChange);
    return () => window.removeEventListener("dp-discovery-changed", onChange);
  }, [agentCode, excludeId]);
  return items;
}

// ---- Wishlist ----

export function getWishlist(agentCode: string): WishlistItem[] {
  return loadList<WishlistItem>(wlKey(agentCode));
}

export function isWishlisted(agentCode: string, productId: number): boolean {
  return getWishlist(agentCode).some((it) => it.wc_product_id === productId);
}

export function toggleWishlist(agentCode: string, item: Omit<WishlistItem, "added_at">): WishlistItem[] {
  const key = wlKey(agentCode);
  const list = loadList<WishlistItem>(key);
  const idx = list.findIndex((it) => it.wc_product_id === item.wc_product_id);
  if (idx >= 0) {
    list.splice(idx, 1);
  } else {
    list.unshift({ ...item, added_at: Date.now() });
  }
  saveList(key, list);
  return list;
}

export function removeFromWishlist(agentCode: string, productId: number): WishlistItem[] {
  const key = wlKey(agentCode);
  const list = loadList<WishlistItem>(key).filter((it) => it.wc_product_id !== productId);
  saveList(key, list);
  return list;
}

export function useWishlist(agentCode: string) {
  const [items, setItems] = useState<WishlistItem[]>(() => getWishlist(agentCode));
  useEffect(() => {
    setItems(getWishlist(agentCode));
    const onChange = (e: Event) => {
      const ev = e as CustomEvent<{ key: string }>;
      if (ev.detail?.key === wlKey(agentCode)) {
        setItems(getWishlist(agentCode));
      }
    };
    window.addEventListener("dp-discovery-changed", onChange);
    return () => window.removeEventListener("dp-discovery-changed", onChange);
  }, [agentCode]);
  return {
    items,
    count: items.length,
    has: (productId: number) => items.some((it) => it.wc_product_id === productId),
    toggle: (item: Omit<WishlistItem, "added_at">) => setItems(toggleWishlist(agentCode, item)),
    remove: (productId: number) => setItems(removeFromWishlist(agentCode, productId)),
  };
}
